import React from 'react';
import { DictionaryType, DictionaryMetadata } from '../../types';
import { FileText, Edit, Upload, Download, Trash2 } from 'lucide-react';

interface DictionaryCardProps {
  type: DictionaryType;
  metadata?: DictionaryMetadata | null;
  onEdit: (type: DictionaryType) => void;
  onImport: (type: DictionaryType) => void;
  onExport: (type: DictionaryType) => void;
  onDelete: (type: DictionaryType) => void;
}

const TYPE_DESCRIPTIONS: Record<DictionaryType, string> = {
  VietPhrase: 'Từ điển cụm từ dịch chính (Trung - Việt)',
  Name: 'Từ điển tên riêng (nhân danh, địa danh)',
  PhienAm: 'Từ điển phiên âm Hán Việt',
  Pronouns: 'Từ điển đại từ xưng hô',
  Rule: 'Từ điển quy tắc thay thế & ngữ pháp',
};

export const DictionaryCard: React.FC<DictionaryCardProps> = ({
  type,
  metadata,
  onEdit,
  onImport,
  onExport,
  onDelete,
}) => {
  const hasData = metadata && metadata.wordCount > 0;
  const formattedCount = hasData
    ? new Intl.NumberFormat('vi-VN').format(metadata.wordCount) + ' từ'
    : 'Chưa thiết lập';

  const formattedDate = metadata?.updatedAt
    ? new Date(metadata.updatedAt).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        color: 'var(--text-on-light)',
        borderRadius: '0.625rem',
        padding: '1rem',
        boxShadow: 'var(--shadow-sm)',
        border: '1px solid var(--border-light)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '190px',
      }}
    >
      <div>
        {/* Header line: Badge & Type */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span
            style={{
              backgroundColor: 'var(--accent-highlight)',
              color: 'var(--text-on-light)',
              fontSize: '0.75rem',
              fontWeight: 700,
              padding: '0.125rem 0.5rem',
              borderRadius: '0.25rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {type}
          </span>
          {formattedDate && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Cập nhật: {formattedDate}
            </span>
          )}
        </div>

        {/* Description */}
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
          {TYPE_DESCRIPTIONS[type]}
        </p>

        {/* Word Count */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.375rem', marginBottom: '1rem' }}>
          <FileText size={18} style={{ color: hasData ? 'var(--action-primary)' : 'var(--text-muted)' }} />
          <span
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: hasData ? 'var(--text-main)' : 'var(--text-muted)',
              fontStyle: hasData ? 'normal' : 'italic',
            }}
          >
            {formattedCount}
          </span>
        </div>
      </div>

      {/* Action Buttons Toolbar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0.375rem',
          paddingTop: '0.5rem',
          borderTop: '1px dashed var(--border-light)',
        }}
      >
        <button
          onClick={() => onEdit(type)}
          title="Xem & Sửa từ"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0.375rem',
            borderRadius: '0.375rem',
            backgroundColor: '#ffffff',
            color: 'var(--text-main)',
            fontSize: '0.6875rem',
            fontWeight: 500,
            border: '1px solid var(--border-light)',
          }}
        >
          <Edit size={15} style={{ color: 'var(--action-primary)' }} />
          <span>Sửa</span>
        </button>

        <button
          onClick={() => onImport(type)}
          title="Nhập (Import)"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0.375rem',
            borderRadius: '0.375rem',
            backgroundColor: '#ffffff',
            color: 'var(--text-main)',
            fontSize: '0.6875rem',
            fontWeight: 500,
            border: '1px solid var(--border-light)',
          }}
        >
          <Upload size={15} style={{ color: 'var(--action-primary)' }} />
          <span>Nhập</span>
        </button>

        <button
          onClick={() => onExport(type)}
          disabled={!hasData}
          title={hasData ? 'Xuất file .txt' : 'Chưa có dữ liệu để xuất'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0.375rem',
            borderRadius: '0.375rem',
            backgroundColor: '#ffffff',
            color: hasData ? 'var(--text-main)' : 'var(--text-muted)',
            fontSize: '0.6875rem',
            fontWeight: 500,
            border: '1px solid var(--border-light)',
            opacity: hasData ? 1 : 0.5,
            cursor: hasData ? 'pointer' : 'not-allowed',
          }}
        >
          <Download size={15} style={{ color: hasData ? 'var(--action-primary)' : 'var(--text-muted)' }} />
          <span>Xuất</span>
        </button>

        <button
          onClick={() => onDelete(type)}
          disabled={!hasData}
          title={hasData ? 'Xóa từ điển' : 'Chưa có dữ liệu để xóa'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0.375rem',
            borderRadius: '0.375rem',
            backgroundColor: '#ffffff',
            color: hasData ? 'var(--action-danger)' : 'var(--text-muted)',
            fontSize: '0.6875rem',
            fontWeight: 500,
            border: '1px solid var(--border-light)',
            opacity: hasData ? 1 : 0.5,
            cursor: hasData ? 'pointer' : 'not-allowed',
          }}
        >
          <Trash2 size={15} style={{ color: hasData ? 'var(--action-danger)' : 'var(--text-muted)' }} />
          <span>Xóa</span>
        </button>
      </div>
    </div>
  );
};
